import { useCallback, useContext, useMemo } from "react";
import { Context } from "./Context";
import { Box, Collapse, gridClasses, LinearProgress } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { Toolbar } from "./Toolbar";
import { ContextMenu } from "components/context_menu/ContextMenu";
import { getBorder } from "utils/Styles";
import { Totals } from "./Totals";
import { useTranslation } from "react-i18next";
import WalletIcon from "@mui/icons-material/Wallet";
import CalculateIcon from "@mui/icons-material/Calculate";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import {
	createPunch,
	disablePunches,
	gettingSimulatePunches,
} from "utils/Timecard";
import { AddPaycode } from "components/dialog/timecard/AddPaycode";

const Table = () => {
	const { t } = useTranslation();

	const {
		rows,
		setRows,
		columns,
		virtualization,
		setVirtualization,
		virtualTotals,
		setVirtualTotals,
		apiRef,
		contextMenu,
		setContextMenu,
		cell,
		setCell,
		employee,
		tableDateRange,
		openTotals,
		setOpenTotals,
		totalsDateRange,
		setTotalsDateRange,
		deleteRealPunches,
		setDeleteRealPunches,
		openAssignPaycode,
		setOpenAssignPaycode,
		loadingGettingSchedule,
		loadingGettingPunchesAndExceptions,
		setLoadingGettingPunchesAndExceptions,
		loadingGettingPaycodes,
		reloading,
	} = useContext(Context);

	const options = useMemo(
		() => [
			{
				privilege: true,
				name: t("timekeeper.timecard.table.menu.assign_paycode"),
				click: () => {
					setContextMenu(null);
					setOpenAssignPaycode(true);
				},
				icon: <WalletIcon />,
				group: 1,
				disabled: false,
				seleted: false,
				onlyOne: false,
				dialog: undefined,
			},
			{
				privilege: true,
				name: t("timekeeper.timecard.table.menu.save_changes"),
				click: () => {
					setContextMenu(null);
					setVirtualization(false);

					const punches = [];

					for (let row of apiRef.current
						.getAllRowIds()
						.map((id) => apiRef.current.getRow(id))) {
						row.punch_in
							.filter((punch) => !punch.missing)
							.map((punch) => {
								const dateTime = moment.tz(
									`${punch.date_time}:00`,
									moment.tz.guess()
								);

								const punch_in = {
									/* dateTime: punch.date_time,
									punchDateTime: dateTime.format(
										"YYYY-MM-DDTHH:mm:ss"
									), */
									id: punch.id,
									punchDateTimeUTC: dateTime
										.utc()
										.format("YYYY-MM-DDTHH:mm:ss"),
									timeZone: moment.tz.guess(),
								};

								return punches.push(punch_in);
							});

						row.punch_out
							.filter((punch) => !punch.missing)
							.map((punch) => {
								const dateTime = moment.tz(
									`${punch.date_time}:00`,
									moment.tz.guess()
								);

								const punch_out = {
									/* dateTime: punch.date_time,
									punchDateTime: dateTime.format(
										"YYYY-MM-DDTHH:mm:ss"
									), */
									id: punch.id,
									punchDateTimeUTC: dateTime
										.utc()
										.format("YYYY-MM-DDTHH:mm:ss"),
									timeZone: moment.tz.guess(),
								};

								return punches.push(punch_out);
							});
					}

					console.log(
						"punches filter",
						punches.filter((punch) => punch.id < 0),
						"punches",
						punches,
						"deleteRealPunches",
						deleteRealPunches
					);

					const promises = punches
						.filter((punch) => punch.id < 0)
						.map((punch) =>
							createPunch(
								setLoadingGettingPunchesAndExceptions,
								{
									punch_dtm: punch.punchDateTimeUTC,
									employee_id:
										employee.length > 0
											? employee[0]?.id
											: undefined,
									device_id: -1,
									in_out: false,
									type_autentification: "finger",
									timezone: punch.timeZone,
									online: true,
								},
								reloading,
								() => {}
							)
						);

					Promise.all(promises)
						.then(() => {
							console.log("All punches created successfully");
							reloading();
						})
						.catch((error) => {
							console.error("Error creating punches:", error);
						});

					if ([...new Set(deleteRealPunches)].length > 0) {
						disablePunches(
							setLoadingGettingPunchesAndExceptions,
							{ ids: [...new Set(deleteRealPunches)] },
							setDeleteRealPunches,
							reloading
						);
					}
				},
				icon: <SaveIcon />,
				group: 1,
				disabled: false,
				seleted: false,
				onlyOne: false,
				dialog: undefined,
			},
			{
				privilege: true,
				name: openTotals
					? t("timekeeper.timecard.table.menu.close_totals")
					: t("timekeeper.timecard.table.menu.open_totals"),
				click: () => {
					console.log("click", !openTotals);
					setContextMenu(null);
					setOpenTotals(!openTotals);
				},
				icon: <CalculateIcon />,
				group: 2,
				disabled: false,
				seleted: false,
				onlyOne: false,
				dialog: undefined,
			},
			{
				privilege: true,
				name: t("timekeeper.timecard.table.menu.reload"),
				click: () => {
					setContextMenu(null);
					reloading();
				},
				icon: <RefreshIcon />,
				group: 3,
				disabled: false,
				seleted: false,
				onlyOne: false,
				dialog: undefined,
			},
		],
		[
			apiRef,
			deleteRealPunches,
			employee,
			openTotals,
			reloading,
			setContextMenu,
			setDeleteRealPunches,
			setLoadingGettingPunchesAndExceptions,
			setOpenAssignPaycode,
			setVirtualization,
			t,
		]
	);

	return (
		<>
			<Box
				sx={{
					[`.${gridClasses.cell}.open`]: {
						backgroundColor: (theme) =>
							theme.palette.background.default,
					},
					[`.${gridClasses.cell}.close`]: {
						backgroundColor: (theme) =>
							theme.palette.background.close,
					},
				}}
			>
				<div
					onContextMenu={(event) => {
						event.preventDefault();

						setContextMenu(
							contextMenu === null
								? {
										mouseX: event.clientX + 2,
										mouseY: event.clientY - 6,
								  }
								: null
						);
					}}
				>
					<DataGridPro
						apiRef={apiRef}
						sx={{
							border: (theme) =>
								`1px solid ${theme.palette.divider}`,
							height: `calc(100vh - ${
								openTotals ? "500px" : "120px"
							})`,
						}}
						slots={{
							toolbar: useCallback(() => <Toolbar />, []),
							loadingOverlay: LinearProgress,
						}}
						slotProps={{
							row: {
								onContextMenu: (e) => {
									setCell({
										column: e?.target?.dataset?.field,
										row: e?.currentTarget
											?.getAttribute("data-id")
											.slice(-10),
										employee_id:
											employee.length > 0
												? employee[0]?.id
												: undefined,
									});
								},
							},
						}}
						onCellEditStop={(params) => {
							console.log("stop", params);

							if (
								params.field === "punch_in" ||
								params.field === "punch_out"
							) {
								setRows(
									apiRef.current
										.getAllRowIds()
										.map((id) => apiRef.current.getRow(id))
								);

								const punches = [];

								for (let row of apiRef.current
									.getAllRowIds()
									.map((id) => apiRef.current.getRow(id))) {
									row.punch_in
										.filter((punch) => !punch.missing)
										.map((punch) => {
											const dateTime = moment.tz(
												`${punch.date_time}:00`,
												moment.tz.guess()
											);

											const punch_in = {
												/* dateTime: punch.date_time,
												punchDateTime: dateTime.format(
													"YYYY-MM-DDTHH:mm:ss"
												), */
												id: punch.id,
												punchDateTimeUTC: dateTime
													.utc()
													.format(
														"YYYY-MM-DDTHH:mm:ss"
													),
												timeZone: moment.tz.guess(),
											};

											return punches.push(punch_in);
										});

									row.punch_out
										.filter((punch) => !punch.missing)
										.map((punch) => {
											const dateTime = moment.tz(
												`${punch.date_time}:00`,
												moment.tz.guess()
											);

											const punch_out = {
												/* dateTime: punch.date_time,
												punchDateTime: dateTime.format(
													"YYYY-MM-DDTHH:mm:ss"
												), */
												id: punch.id,
												punchDateTimeUTC: dateTime
													.utc()
													.format(
														"YYYY-MM-DDTHH:mm:ss"
													),
												timeZone: moment.tz.guess(),
											};

											return punches.push(punch_out);
										});
								}

								const body = {
									start_dtm: tableDateRange[0].format(
										"YYYY-MM-DDTHH:mm:ss"
									),
									end_dtm: tableDateRange[1]
										.add(23, "hours")
										.add(59, "minutes")
										.add(59, "seconds")
										.format("YYYY-MM-DDTHH:mm:ss"),
									employee_id:
										employee.length > 0
											? employee[0]?.id
											: undefined,
									punches: punches,
								};

								console.log("punches", punches);

								gettingSimulatePunches(
									setLoadingGettingPunchesAndExceptions,
									body,
									setRows,
									setVirtualization,
									setVirtualTotals
								);

								console.log("rows post reaload", rows);
							}

							/* if (params.field === "paycodes") {
								console.log("stop", params);
							} */
						}}
						/* getCellClassName={(params) => {
							if (
								!moment(
									params?.row?.date,
									"YYYY-MM-DD"
								).isValid()
							) {
								return "";
							}

							const date1 = moment(
								periods.find(
									(period) =>
										period.person_number ===
										params?.row?.person_number
								)?.end_date,
								"YYYY-MM-DD"
							);
							const date2 = moment(
								params?.row?.date,
								"YYYY-MM-DD"
							);

							const style = date1.isSameOrAfter(date2);

							return style ? "close" : "open";
						}} */
						getRowHeight={(params) => {
							const { model } = params;

							let length = 64;

							for (const key of Object.keys(model).filter((k) =>
								Array.isArray(model[k])
							)) {
								if (key !== "path") {
									let counter = 0;

									if (model[key].length > 0) {
										counter =
											counter + model[key].length * 55;
									}

									if (counter > length) {
										length = counter;
									}
								}
							}

							return length;
						}}
						/* isCellEditable={(params) =>
							params?.row?.open_period?.open
						} */
						columns={columns}
						rows={rows}
						showCellVerticalBorder
						showColumnVerticalBorder
						loading={
							loadingGettingSchedule ||
							loadingGettingPunchesAndExceptions ||
							loadingGettingPaycodes
						}
						hideFooter
						disableRowSelectionOnClick
						disableColumnMenu
					/>
				</div>

				<ContextMenu
					contextMenu={contextMenu}
					setContextMenu={setContextMenu}
					options={options}
					checked={[]}
				/>

				<AddPaycode
					open={openAssignPaycode}
					setOpen={setOpenAssignPaycode}
					employee={employee.length > 0 ? employee[0] : undefined}
					cell={cell}
				/>

				<Collapse
					sx={{
						height: "55vh",
						mt: 2.5,
						ml: -2.5,
						mr: -2.5,
						p: 2.5,
						borderTop: (theme) => getBorder(theme.palette.mode),
					}}
					in={openTotals}
					timeout="auto"
					unmountOnExit
				>
					<Totals
						open={openTotals}
						range={totalsDateRange}
						setRange={setTotalsDateRange}
						employee={employee.length > 0 ? employee[0] : undefined}
						virtualization={virtualization}
						virtualTotals={virtualTotals}
					/>
				</Collapse>
			</Box>
		</>
	);
};

export { Table };
